import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalCheckbox, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { FormField } from '../../../../models/built-form';

interface EmailConfigModel {
  label: string;
  placeholder: string;
  helpText: string;
  required: boolean;
  customMessage: string;
}

@Component({
  selector: 'app-email-config-view',
  standalone: true,
  imports: [SignalFormField, SignalTextField, SignalCheckbox, SignalFormDirective],
  templateUrl: './email-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly model = signal<EmailConfigModel>({
    label: '',
    placeholder: '',
    helpText: '',
    required: false,
    customMessage: '',
  });
  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      this.model.set({
        label: next.label,
        placeholder: next.placeholder ?? '',
        helpText: next.helpText ?? '',
        required: next.required ?? validation.required ?? false,
        customMessage: validation.customMessage ?? '',
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        placeholder: draft.placeholder.trim() || undefined,
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        validation: {
          required: draft.required,
          customMessage: draft.customMessage.trim() || undefined,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
